import React, { useRef } from 'react';
import { useField } from 'formik';
import cn from 'classnames';

import { MediaMultiType, MediaType } from 'client/types';
import Icon from 'client/components/common/Icon';
import Error from '../../../Error';

import css from './UploadMedia.module.scss';
interface Props {
  name: string;
  multi?: boolean;
  accept?: string;
  value?: MediaType | MediaMultiType;
  fakeBtn: string;
  acceptText: string;
}

const UploadMedia = (props: Props) => {
  const { name, accept = '', multi = false, fakeBtn, acceptText } = props;
  const inputRef = useRef<HTMLInputElement>(null);
  const [field, meta, helpers] = useField<MediaType | MediaMultiType>(name);
  const hasError = meta.error && meta.touched;
  const errorMsg = React.useMemo(() => {
    const isMetaErrorArray = Array.isArray(meta.error);

    if (isMetaErrorArray) return '';

    return !!(meta.touched && meta.error) ? meta.error : '';
  }, [meta.touched, meta.error]);

  const getErrorMsgByIndex = React.useCallback(
    (index: number) => {
      const isMetaErrorArray = Array.isArray(meta.error);

      if (isMetaErrorArray) {
        return meta.error ? meta.error[index] ?? '' : '';
      }

      return '';
    },
    [meta.error]
  );

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.currentTarget.files;

    if (files && files?.length !== 0) {
      if (multi) {
        helpers.setValue([...((field.value as MediaMultiType) ?? []), ...Array.from(files)]);
      } else {
        helpers.setValue(files[0]);
      }
    }
  };

  const mediaName = (value: MediaType | MediaMultiType) => {
    let name = '';

    if (typeof value === 'string') {
      name = value.split('?')[0].split('/').pop() || '';
    } else {
      name = (value as File).name || '';
    }

    return name;
  };

  const onClearBtnClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const { index } = e.currentTarget.dataset;

    if (multi) {
      helpers.setValue((field.value as MediaMultiType)?.filter((f, i) => Number(index) !== i) ?? null);
    } else {
      helpers.setValue(null);
    }
    helpers.setTouched(true);

    if (inputRef.current) inputRef.current.value = '';
  };

  const renderMediaInfo = () => {
    if (!field.value) return null;

    if (multi) {
      return (field.value as MediaMultiType).map((val, index) => {
        const errorMsg = getErrorMsgByIndex(index);
        const fileName = mediaName(val);
        const isFieldWithError = Boolean(errorMsg && meta.touched);

        return (
          <React.Fragment key={fileName}>
            <div className={cn(css.file, isFieldWithError && css.error)}>
              <div className={css.fileWrapper}>
                {mediaName(val)}
                <div className={css.trashBtn} data-index={index} onClick={onClearBtnClick}>
                  <Icon type="trash" />
                </div>
              </div>
              <div className={css.blurredBlock} />
            </div>

            {errorMsg && meta.touched && (
              <div className={css.errorWrapper}>
                <Error name={name} error={errorMsg} />
              </div>
            )}
          </React.Fragment>
        );
      });
    }

    return (
      <div className={css.file}>
        <div className={css.fileWrapper}>
          {mediaName(field.value)}
          <div className={css.trashBtn} onClick={onClearBtnClick}>
            <Icon type="trash" />
          </div>
        </div>
        <div className={css.blurredBlock} />
      </div>
    );
  };

  return (
    <div className={cn(css.mediaWrapper, hasError && css.error)}>
      <label>
        <input type="file" multiple={multi} name={name} onChange={onChange} ref={inputRef} accept={accept} />
        <div className={css.fakeBtn}>{fakeBtn}</div>
        <div className={css.acceptFilesText} dangerouslySetInnerHTML={{ __html: acceptText }}></div>
      </label>
      {renderMediaInfo()}
      {errorMsg && meta.touched && (
        <div className={css.errorWrapper}>
          <Error name={name} error={errorMsg} />
        </div>
      )}
    </div>
  );
};

export default UploadMedia;
